import { bytesToBase64 } from './pcm';
import { makeResampler } from './resample';

/**
 * LiveAvatar (HeyGen), Avatar Only mode: the face streams into a LiveKit
 * room the browser joins to watch, and the examiner's audio goes to it over
 * the session's WebSocket as utterances (docs.liveavatar.com/docs/lite-mode).
 * The library is loaded only when this trial is switched on.
 */
const CHUNK = 48000; // one second of 24 kHz PCM16, the size LiveAvatar recommends
const RATE = 24000;

function newId() {
  return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

let library = null;
/** Fetch the library once; the start screen calls this so Begin needn't wait for it. */
export function loadLiveAvatar() {
  if (!library) {
    library = import('livekit-client');
    library.catch(() => { library = null; });
  }
  return library;
}

export async function createLiveAvatarRenderer(session, { video, audio }) {
  const { Room, RoomEvent, Track } = await loadLiveAvatar();
  const room = new Room();
  room.on(RoomEvent.TrackSubscribed, (track) => {
    if (track.kind === Track.Kind.Video) track.attach(video);
    else if (track.kind === Track.Kind.Audio) track.attach(audio);
  });

  const pending = new Map();
  const done = (id) => {
    const p = pending.get(id);
    if (!p) return;
    clearTimeout(p.timer);
    pending.delete(id);
    p.resolve();
  };

  // The room (the face) and the WebSocket (the voice) connect side by side.
  const ws = new WebSocket(session.ws_url);
  const joined = room.connect(session.livekit_url, session.livekit_client_token);
  const opened = new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('LiveAvatar did not connect')), 15000);
    ws.onmessage = (message) => {
      let event;
      try { event = JSON.parse(message.data); } catch { return; }
      if (event.type === 'session.state_updated' && event.state === 'connected') {
        clearTimeout(timer);
        resolve();
      } else if (event.type === 'agent.speak_ended' || event.type === 'agent.speak_interrupted') {
        done(event.source_event_id);
      }
    };
    ws.onerror = () => { clearTimeout(timer); reject(new Error('LiveAvatar connection failed')); };
  });
  try {
    await Promise.all([joined, opened]);
  } catch (err) {
    try { ws.close(); } catch { /* never opened */ }
    try { await room.disconnect(); } catch { /* never joined */ }
    throw err;
  }
  // The session ends after five idle minutes without this.
  const keepAlive = setInterval(() => {
    if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'session.keep_alive' }));
  }, 60000);

  return {
    provider: 'liveavatar',
    sampleRate: 24000,
    say(pcm, seconds) {
      const id = newId();
      return new Promise((resolve) => {
        pending.set(id, { resolve, timer: setTimeout(() => done(id), (seconds + 5) * 1000) });
        for (let i = 0; i < pcm.length; i += CHUNK) {
          ws.send(JSON.stringify({ type: 'agent.speak', event_id: id, audio: bytesToBase64(pcm.subarray(i, i + CHUNK)) }));
        }
        ws.send(JSON.stringify({ type: 'agent.speak_end', event_id: id }));
      });
    },
    /** A line as it streams in: push() each Int16Array piece at sourceRate,
     * then end(), which resolves once the face has finished saying it. Sent
     * in one-second blocks, the size LiveAvatar recommends: quarter-second
     * ones ended lines early in a sandbox test. */
    open(sourceRate) {
      const resample = makeResampler(sourceRate, RATE);
      const id = newId();
      let held = [];
      let heldSamples = 0;
      let samples = 0;
      const line = new Promise((resolve) => { pending.set(id, { resolve, timer: null }); });
      const flush = () => {
        if (!heldSamples) return;
        const block = new Int16Array(heldSamples);
        let at = 0;
        held.forEach((p) => { block.set(p, at); at += p.length; });
        held = [];
        heldSamples = 0;
        ws.send(JSON.stringify({ type: 'agent.speak', event_id: id, audio: bytesToBase64(new Uint8Array(block.buffer)) }));
      };
      return {
        push(piece) {
          const out = resample(piece);
          samples += out.length;
          held.push(out);
          heldSamples += out.length;
          if (heldSamples * 2 >= CHUNK) flush();
        },
        end() {
          flush();
          ws.send(JSON.stringify({ type: 'agent.speak_end', event_id: id }));
          const p = pending.get(id);
          if (p) p.timer = setTimeout(() => done(id), (samples / RATE + 5) * 1000);
          return line;
        },
      };
    },
    interrupt() {
      if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'agent.interrupt' }));
      [...pending.keys()].forEach(done);
    },
    async stop() {
      clearInterval(keepAlive);
      [...pending.keys()].forEach(done);
      try { ws.close(); } catch { /* already closed */ }
      try { await room.disconnect(); } catch { /* already left */ }
    },
  };
}
